/**
 * Class for DatabaseData
 * @class
 * @classdesc Class for working with the database and getting data for
 * "Data Pages" such as:
 * https://www.recenter.tamu.edu/data/housing-activity/
 * https://www.recenter.tamu.edu/data/housing-affordability/
 * https://www.recenter.tamu.edu/data/home-price-index/
 * https://www.recenter.tamu.edu/data/rural-land/
 * https://www.recenter.tamu.edu/data/building-permit/
 * https://www.recenter.tamu.edu/data/employment/
 * https://www.recenter.tamu.edu/data/population/
 */

class DataDatabase {
    /**
     * Correlation of database names, page slug and data block name
     *
     * @typedef {Object} TableResource
     * @property {!string} name - name table without prefix
     * @property {!string} slug - slug page. Example housing-activity
     * @property {!string} apiSlug - API slug. Example recdata
     * @property {!string} idSlug - ID slug. Example geo_id
     * @property {!string} geoType - geoType. Example geo_id
     *
     * @type {Array.<TableResource>}
     */

    TABLES_RESOURCES = [
        {
            name: 'housing-activity',
            slug: 'housing-activity',
            apiSlug: 'housing-activity',
            idSlug: 'geo_id',
            geoType: 'geo_type',
        },
        {
            name: 'housing-report',
            slug: 'housing-report',
            apiSlug: 'housing-report',
            idSlug: 'geo_id',
            geoType: 'geo_type',
        },
        {
            name: 'home-price-index',
            slug: 'home-price-index',
            apiSlug: 'home-price-index',
            idSlug: 'geo_id',
            geoType: 'geo_type',
        },
        {
            name: 'building-permit',
            slug: 'building-permit',
            apiSlug: 'building-permit',
            idSlug: 'geo_id',
            geoType: 'geo_type',
        },
        {
            name: 'employment',
            slug: 'employment',
            apiSlug: 'employment',
            idSlug: 'geo_id',
            geoType: 'geo_type',
        },
        {
            name: 'employment-twc',
            slug: 'employment-twc',
            apiSlug: 'employment-twc',
            idSlug: 'geo_id',
            geoType: 'geo_type',
        },
        {
            name: 'rural-land',
            slug: 'rural-land',
            apiSlug: 'rural-land',
            idSlug: 'geo_id',
            geoType: 'geo_type',
        },
        {
            name: 'population',
            slug: 'population',
            apiSlug: 'population',
            idSlug: 'geo_id',
            geoType: 'geo_type',
        },
        {
            name: 'housing-affordability',
            slug: 'housing-affordability',
            apiSlug: 'housing-affordability',
            idSlug: 'geo_id',
            geoType: 'geo_type',
        },
    ];

    /**
     * Base API URL
     * @type {String}
     */

    BASE_API_URL = 'wp-json/trerc-data/v1';

    /**
     * Current Slug Page
     * @type {String}
     */

    currentSlugPage = '';

    /**
     * @param {!String} slug - slug page. Example housing-activity
     */
    constructor(slug) {
        this.currentSlugPage = slug;
    }

    /**
     * Get Data use ID
     * @param {Number} id - location id
     * @param {String} geo_type - geo type
     * @return {Array.<Object>|Array|null}
     */

    async getDataById(id, geo_type) {
        let tableResource = this.TABLES_RESOURCES.find(
            (el) => el.slug === this.currentSlugPage,
        );

        if (!tableResource) {
            throw new Error(
                `Table resource not found for slug: ${this.currentSlugPage}`,
            );
        }

        let requestUrl =
            `${window.location.origin}/` +
            `${this.BASE_API_URL}/` +
            `${tableResource.apiSlug}?` +
            `${tableResource.idSlug}=${id}&` +
            `${tableResource.geoType}=${geo_type}`;

        try {
            let response = await fetch(requestUrl);
            return await response.json();
        } catch (e) {
            return null;
        }
    }

    /**
     * Get Selected Data
     * @return {Array.<Object>|Array|null}
     */

    async getSelectedData() {
        let tableResource = this.TABLES_RESOURCES.find(
            (el) => el.slug === this.currentSlugPage,
        );

        if (!tableResource) {
            throw new Error(
                `Table resource not found for slug: ${this.currentSlugPage}`,
            );
        }

        let requestUrl =
            `${window.location.origin}/` +
            `${this.BASE_API_URL}/` +
            `${tableResource.apiSlug}?` +
            `selects`;

        try {
            let response = await fetch(requestUrl);
            return await response.json();
        } catch (e) {
            return null;
        }
    }
}

export default DataDatabase;
